import { useCallback, useState } from 'react';
import {
  ILogReport,
  ILogReportFilter,
  IReportSummary,
} from '../screens/report/types/report-log.type';
import { httpRequest } from '../helpers/api';
import { generateQueryString, IHttpResponse } from '@qlibs/react-components';
import { createGlobalState } from 'react-hooks-global-state';
import { AssetProperties } from '../screens/asset/types/asset.type';
import { DeviceProperties } from '../screens/device/types/device.type';

/** The filter, asset and devices the loaded logs were requested with. */
export interface IAppliedReport {
  filter: ILogReportFilter;
  timezone: number;
  asset: AssetProperties;
  devices: DeviceProperties[];
}

interface IReportLog {
  loadingLog: boolean;
  loadingDevice: boolean;
  showReportTableConfig: boolean;
  showReportDownloadConfig: boolean;
  checklistColumn: any[];
  dataCompletion: any[];
  logs: ILogReport;
  // summary of the logs from the backend, null when it is calculated from them
  reportSummary: IReportSummary | null;
  // null until the first logs arrive
  appliedReport: IAppliedReport | null;
  engineEvents: any;
  clutchEvents: any;
  speedUnit: string;
  distanceUnit: string;
  cruiseUnit: string;
  literUnit: string;
}

const initialState: IReportLog = {
  loadingLog: false,
  loadingDevice: false,
  showReportTableConfig: false,
  showReportDownloadConfig: false,
  checklistColumn: [],
  dataCompletion: [],
  logs: {},
  reportSummary: null,
  appliedReport: null,
  engineEvents: null,
  clutchEvents: null,
  speedUnit: 'KNOT',
  distanceUnit: 'NM',
  literUnit: 'Liter/Min',
  cruiseUnit: 'NM',
};

const { useGlobalState } = createGlobalState(initialState);

const useReportLog = () => {
  const [logs, setLogsState] = useGlobalState('logs');
  const [reportSummary, setReportSummary] = useGlobalState('reportSummary');
  const [appliedReport, setAppliedReport] = useGlobalState('appliedReport');
  // new logs drop the summary and the filter of the old ones, set them after
  // the logs
  const setLogs = useCallback(
    (value: ILogReport) => {
      setLogsState(value);
      setReportSummary(null);
      setAppliedReport(null);
    },
    [setLogsState, setReportSummary, setAppliedReport],
  );
  const [engineEvents, setEngineEvents] = useGlobalState('engineEvents');
  const [clutchEvents, setClutchEvents] = useGlobalState('clutchEvents');
  const [loadingLog, setLoadingLog] = useGlobalState('loadingLog');
  const [loadingDevice, setLoadingDevice] = useGlobalState('loadingDevice');
  const [showReportTableConfig, setShowReportTableConfig] = useGlobalState(
    'showReportTableConfig'
  );
  const [showReportDownloadConfig, setShowReportDownloadConfig] =
    useGlobalState('showReportDownloadConfig');
  const [checklistColumn, setChecklistColumn] =
    useGlobalState('checklistColumn');
  const [dataCompletion, setDataCompletion] = useGlobalState('dataCompletion');
  const [speedUnit, setSpeedUnit] = useGlobalState('speedUnit');
  const [distanceUnit, setDistanceUnit] = useGlobalState('distanceUnit');
  const [literUnit, setLiterUnit] = useGlobalState('literUnit');
  const [cruiseUnit, setCruiseUnit] = useGlobalState('cruiseUnit');

  return {
    setSpeedUnit,
    speedUnit,
    logs,
    reportSummary,
    setReportSummary,
    appliedReport,
    setAppliedReport,
    engineEvents,
    setEngineEvents,
    clutchEvents,
    setClutchEvents,
    loadingLog,
    setLogs,
    setShowReportTableConfig,
    showReportTableConfig,
    checklistColumn,
    setChecklistColumn,
    setDataCompletion,
    dataCompletion,
    setLoadingLog,
    setShowReportDownloadConfig,
    showReportDownloadConfig,
    loadingDevice,
    setLoadingDevice,
    setDistanceUnit,
    setLiterUnit,
    distanceUnit,
    literUnit,
    setCruiseUnit,
    cruiseUnit,
  };
};

export default useReportLog;
